import { useMemo } from 'react';
import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import { useNavigationGlobalResource } from '@atlassian/jira-navigation-apps-sidebar-common';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouter } from '@atlassian/jira-router';

import type { ConnectDataItem } from '../../common/types';
import { getLinks, transformLink, findItemAndStack } from '../../common/utils';
import type { SearchResult } from './types';

// use memoised functions per hook to avoid re-computation between same hooks
const getItemsMemo = memoizeOne((d) =>
	getLinks(d, ['global-navigation-product'])
		.map(transformLink)
		.filter((item) => item != null && item.items),
);
const findItemAndStackMemo = memoizeOne(findItemAndStack);

export const useGlobalConnectItems = () => {
	const [{ route, location }] = useRouter();
	const { data, loading, error } = useNavigationGlobalResource();

	const sidebars = useMemo(() => data && getItemsMemo(data), [data]);

	if (ff('small-temporary-analytic-for-new-nav_ws39b')) {
		// FF hooks as recommended in https://hello.atlassian.net/wiki/spaces/TGR/pages/3178472787/OPEN+FOR+FEEDBACK+Feature+flagging+hooks#Option-4%3A-Official-Tangerine-guidance-to-use-eslint-disable
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { createAnalyticsEvent } = useAnalyticsEvents();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useMemo(
			() => reportConnectAppsMaxDepth(sidebars, createAnalyticsEvent),
			[sidebars, createAnalyticsEvent],
		);
	}

	const searchResult = useMemo<SearchResult>(() => {
		let result = { stack: [], selected: null, title: null, data: null };
		if (sidebars != null) {
			sidebars.some((sidebar) => {
				const { items } = sidebar || {};
				if (!sidebar || !route || items == null) return false;

				const found = findItemAndStackMemo(items, route.name, location);
				if (found) {
					// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'. | TS2322 - Type 'ConnectDataItem[]' is not assignable to type 'null'.
					result = { ...found, title: sidebar.name, data: items };
					return true;
				}
				return false;
			});
		}
		return result;
	}, [sidebars, route, location]);

	return { ...searchResult, loading, error };
};

// temporary code related to building Nav v4
// Get infos about actual usage of sidebar items by connect apps
// We need this to properly design the horizontal nav version
type LooseConnectDataItem = ConnectDataItem | null | undefined;
interface WithConnectDataItems {
	id?: string;
	items?: Array<LooseConnectDataItem> | null | undefined;
}
function getMaxConnectDataItemsDepth(
	item: WithConnectDataItems | null | undefined,
	encounteredItems: Set<WithConnectDataItems>,
): number {
	try {
		if (!item) return 0;
		if (encounteredItems.has(item)) {
			// loop detected
			return 0;
		}
		if (encounteredItems.size >= 42) {
			// 42 = arbitrary limit
			// either huge size OR loop
			// = most likely we don't understand how the data is structured...
			return 0;
		}
		encounteredItems.add(item);

		const childItems = item.items || [];
		return (
			1 +
			childItems.reduce((acc, childItem) => {
				const subDepth = getMaxConnectDataItemsDepth(childItem, encounteredItems);
				return subDepth > acc ? subDepth : acc;
			}, 0)
		);
	} catch (err) {
		// should never happen, we will investigate if it happens
		return -1;
	}
}

function getConnectAppId(encounteredItems: Set<WithConnectDataItems>): string {
	try {
		return (
			Array.from(encounteredItems).reduce<string | undefined>((acc, item) => {
				if (acc) return acc; // id already found
				if (!item?.id) return acc;
				let candidateId = item.id; // seen ids:
				// com.atlassian.plugins.atlassian-connect-plugin:catlassian__catlassian-dropdown
				// catlassian__catlassian-section-nested-top
				if (candidateId.includes(':')) {
					// so we drop the prefix if any
					candidateId = candidateId.split(':')[1];
				}
				if (candidateId.includes('__')) {
					// drop the suffix if any
					candidateId = candidateId.split('__')[0];
				}

				return candidateId;
			}, '') || '???'
		);
	} catch (err) {
		// should never happen, we will investigate if it happens
		return 'ERROR';
	}
}

function reportConnectAppsMaxDepth(
	sidebars: WithConnectDataItems['items'],
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
): void {
	if (!sidebars) return;

	const encounteredItems = new Set<WithConnectDataItems>();
	const maxDepth = getMaxConnectDataItemsDepth({ items: sidebars }, encounteredItems);
	if (encounteredItems.size === 0) {
		// no sidebar items, no need to report
		return;
	}

	const id = getConnectAppId(encounteredItems);
	fireOperationalAnalytics(createAnalyticsEvent({}), 'connectAppSidebar rendered', {
		maxDepth,
		encounteredCount: encounteredItems.size,
		id,
	});
}
